/**
 * CONSULTANT HUB — service layer for api/consultant-hub.js (docs/CONSULTANT_HUB.md).
 *
 * Two audiences:
 *   - Public: /consultant-hub?t=<token> — one consultant team member's own Hub. Their PIN is
 *     checked once per browser; the signed proof is cached in localStorage under
 *     'chub-proof:<token>' (BackgroundPage.jsx reads the same key to attribute release visits).
 *   - Team (Clerk): the outline's Consultant Hub node and its Add people modal.
 */

const ENDPOINT = '/api/consultant-hub'
const proofKey = (t) => 'chub-proof:' + t

export function getStoredProof(token) {
  try { return localStorage.getItem(proofKey(token)) || '' } catch { return '' }
}
function storeProof(token, proof) {
  try { localStorage.setItem(proofKey(token), proof) } catch { /* private window: re-asked next visit */ }
}

async function readJson(res) {
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = new Error(data.error || ('Request failed (' + res.status + ')'))
    err.code = data.error || 'error'
    err.status = res.status
    throw err
  }
  return data
}

/* ── Public ── */

/** -> { hub } or { pinRequired, name } */
export async function getHub(token) {
  const proof = getStoredProof(token)
  return readJson(await fetch(ENDPOINT + '?t=' + encodeURIComponent(token), proof ? { headers: { 'x-pin-proof': proof } } : undefined))
}

async function publicPost(body) {
  return readJson(await fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }))
}

/** Verify a PIN; stores the proof on success. Throws with .code 'pin_invalid' | 'locked'. */
export async function submitHubPin(token, pin) {
  const { proof } = await publicPost({ action: 'pin', t: token, pin })
  storeProof(token, proof)
  return true
}

export async function requestHubPinReset(token) {
  return publicPost({ action: 'requestReset', t: token })
}

export async function setHubSubscribed(token, on) {
  return publicPost({ action: 'subscribe', t: token, proof: getStoredProof(token), on })
}

/* ── Team (Clerk) ── */

async function teamPost(action, body) {
  const token = await window.__clerkGetToken()
  return readJson(await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ action, ...body }),
  }))
}

/** The team's preview of a project's Hub (no person, nothing recorded). */
export async function previewHub(projectId) {
  const token = await window.__clerkGetToken()
  return readJson(await fetch(ENDPOINT + '?project=' + encodeURIComponent(projectId), { headers: { Authorization: 'Bearer ' + token } }))
}

/** -> { people, releases } — releases carry per-person activity */
export async function listHub(projectId) {
  return teamPost('list', { projectId })
}

/** people: [{ name, email, contactId, orgId, orgName, discipline }] -> { added, skipped } */
export async function addHubPeople(projectId, people) {
  return teamPost('add', { projectId, people })
}

export const revokeHubPerson = (id) => teamPost('revoke', { id })
export const reactivateHubPerson = (id) => teamPost('reactivate', { id })
export const deleteHubPerson = (id) => teamPost('delete', { id })
export const resendHubAccess = (id) => teamPost('resend', { id })
export const regenerateHubAccess = (id) => teamPost('regenerate', { id })
export const setHubPersonDiscipline = (id, discipline) => teamPost('setDiscipline', { id, discipline })

/** Background lens keys a person's release page opens to first. */
export const HUB_DISCIPLINES = [
  { key: 'structural', label: 'Structural' },
  { key: 'mep', label: 'MEP' },
  { key: 'civil', label: 'Civil' },
  { key: 'landscape', label: 'Landscape' },
  { key: 'interiors', label: 'Interior Designer' },
]

/** Best guess of a discipline from a Project Consultants "Consultant Service" value. */
export function disciplineForService(service) {
  const s = String(service || '').toLowerCase()
  if (s.includes('structural')) return 'structural'
  if (s.includes('mechanical') || s.includes('electrical') || s.includes('energy') || s.includes('plumbing')) return 'mep'
  if (s.includes('civil') || s.includes('surveyor') || s.includes('soils')) return 'civil'
  if (s.includes('landscape')) return 'landscape'
  if (s.includes('interior')) return 'interiors'
  return null
}
